// WO-GF-001-P1B-S33 · F192 — THE ONE SOURCE FOR MAP PIN MARKERS.
//
// FOUNDER FINDING (Phase-1B walk, 2026-07-27): the markers are wrong. He wants "a pin with a mountain in
// it", and ruled "i dont want fill on the pins". What was on the map was `mapMountainActive.svg` (a rounded
// triangle) and `mapMarkerPlane.svg` (a circle) — glyphs that float, with nothing pointing at a location.
//
// ⚠ WHERE THESE VECTORS COME FROM, AND WHY THAT CHANGED.
// An earlier cut of this module rebuilt the pin from `public/_mirror/map-markers/*.svg` plus a hand-authored
// teardrop path, because no Figma file had arrived yet. One has. The kit now carries the real frame at
//   <kit>/FIGMA-REFS-2026-07-27/PINS-AND-INFO-BUBBLES.svg   (Figma V3-2022-PHASE-1, node 20468-3)
// and its own index says, verbatim: "This supersedes 'recreate the pins from the snowbus.com landing page'
// ... Build from these vectors." So every path, radius and colour below is COPIED OUT OF THAT FILE — not
// redrawn, not sampled from a screenshot, not reconstructed from the mirror. Nothing here is invented.
//
// Every geometric constant was MEASURED with getBBox() in a real engine against that file, never read off a
// viewBox: Figma insets its ink inside the viewBox, so viewBox-derived scaling puts a small glyph high and
// left of where it belongs. The measured numbers are recorded beside each constant so the next session can
// re-verify them against the same file instead of trusting this comment.
//
// ONE MODULE, NOT PER-CALLSITE (the WO is explicit): this is the first citizen of the asset-registry
// direction. A caller asks for a CATEGORY and a STATE; it never names a hex or a path. Adding a category is
// one entry here rather than a hunt through map components.

// S36 card 1 — THE STATUS COLOURS ARE IMPORTED, NOT RE-DECLARED. `mountain-catalogue` is this estate's
// ONE source for what bookable and info-only look like (S35 · F226), and the founder's ruling is that the
// list and the map must agree row-for-row. Two hexes that happen to match today are exactly the drift
// this module was created to end, so the direction of the dependency is deliberate: the catalogue owns
// the FACT, this module owns how it is DRAWN.
import { STATUS_GREEN, STATUS_YELLOW } from "@/lib/mountain-status-colors";

/** The marker categories the map can render. Extend here, never at a call site. */
export type PinKind = "mountain" | "airport" | "stop";

/**
 * How the mark is treated. The reference carries the same silhouette in both, which is the whole point:
 * fill-vs-outline and colour carry CATEGORY and STATE, the shape never changes.
 *   • `filled`  — solid body in the category colour, glyph knocked out in white.
 *   • `outline` — body stroked in the category colour, basemap showing through (the founder's ruling).
 *   • `muted`   — the reference's grey treatment, for a point with no data behind it.
 */
export type PinTreatment = "filled" | "outline" | "muted";

/** `pin` = the teardrop that points at a spot. `mark` = the glyph-only badge the reference also shows. */
export type PinShape = "pin" | "mark";

/**
 * S36 card 1 — THE STATUS THE GLYPH CARRIES. Founder ruling 2026-07-28: "the mountain glyph INSIDE each
 * map pin carries the status colour — mountains that are yellow in the list are yellow on the map, greens
 * are green. Pin background stays WHITE, pin outline stays PINK." `undefined` means the served
 * bookability has not landed (or the fetch failed), and the glyph then keeps the neutral category colour
 * — the same three-state honesty the list applies by rendering NO lamp at all. A pin cannot claim a
 * status the catalogue has not stated.
 */
export type PinStatus = "bookable" | "info-only";

export type PinOptions = { treatment?: PinTreatment; shape?: PinShape; status?: PinStatus };

// ─────────────────────────────────────────────────────────────────────────────
// PALETTE — read directly off PINS-AND-INFO-BUBBLES.svg, not from the site accent.
// The frame uses exactly five marker colours. An earlier cut guessed #C9372E/#7FAFBC from a render; both
// were wrong, which is why these now cite the element they came from.
// ─────────────────────────────────────────────────────────────────────────────
const PINK = "#EF0761"; // `Polygon 86_2` / `Polygon 86_3` — the mountain marks
const TEAL = "#71ABB8"; // `Ellipse 260` + `Vector_19` — the airport marks
const RED = "#D82028"; // `Union_6` / `Union_10` — the transport pins
const GREY = "#828282"; // `Polygon 86` — the inactive mountain mark
const WHITE = "#fff";

const PIN_COLOUR: Record<PinKind, string> = {
  mountain: PINK,
  airport: TEAL,
  stop: RED,
};

const STATUS_COLOUR: Record<PinStatus, string> = {
  bookable: STATUS_GREEN,
  "info-only": STATUS_YELLOW,
};

// ─────────────────────────────────────────────────────────────────────────────
// THE TEARDROP — `Union_6`, verbatim. Measured bbox 193 424.645 46 54.286.
//
// The head is a true circle: the arc's control points are symmetric about x=216 and the bbox is 46 wide,
// so centre (216, 447.645) and r=23. That is how the glyph gets placed without any hand-tuning.
//
// ONE FRAME FOR BOTH TREATMENTS. The kit draws its outline pins as stroke-expanded fills (`Union_10`,
// bbox 50 x 58.294) — exactly 4 units larger than the filled pin in both axes, i.e. a 4-unit stroke
// centred on the same path. So rather than carry a second 1,400-character path, `outline` strokes THIS
// path at 4 and the viewBox is pre-expanded by 2 on every side. Filled and outline therefore occupy an
// identical footprint, and switching state cannot nudge a pin off its coordinate.
// ─────────────────────────────────────────────────────────────────────────────
const PIN_PATH =
  "M200.514 464.65C200.25 464.41 199.991 464.162 199.737 463.908C190.754 454.925 190.754 440.363 " +
  "199.737 431.381C208.719 422.399 223.281 422.399 232.263 431.381C241.246 440.363 241.246 454.925 " +
  "232.263 463.908C232.009 464.162 231.75 464.41 231.486 464.65C231.406 464.754 231.319 464.855 " +
  "231.223 464.95L218.121 478.052C216.95 479.223 215.05 479.223 213.879 478.052L200.777 464.95C200.681 " +
  "464.855 200.594 464.754 200.514 464.65Z";
const PIN_STROKE = 4;
/** filled bbox 193 424.645 46 54.286, grown by half the stroke on each side. */
const PIN_VIEWBOX = "191 422.645 50 58.286";
const PIN_ASPECT = 50 / 58.286;

// The pin head, derived above. The glyph box is 26 units — the kit's own bus glyph measures 26.264 wide
// and sits centred on the head, so this reproduces its placement rather than choosing a new one.
const HEAD_CX = 216;
const HEAD_CY = 447.645;
const GLYPH_BOX = 26;

// ─────────────────────────────────────────────────────────────────────────────
// GLYPHS — verbatim kit markup, with the two colours swapped for tokens so one copy serves every state.
//   {INK}   = the mark's own colour     {PAPER} = the contrast behind it
// On a FILLED pin the body is the category colour, so ink is white and paper is the category colour.
// On an OUTLINE pin the basemap shows through, so ink is the category colour and paper is white.
// Each glyph's `box` is its measured getBBox(); it becomes the nested <svg>'s viewBox, so the fitting is
// done by preserveAspectRatio ("xMidYMid meet") and there is no scale/translate arithmetic to get wrong.
// ─────────────────────────────────────────────────────────────────────────────
type Glyph = { box: string; markup: string };

/** `Group 11736` (`Polygon 86_4` + `Union_14`) — the mountain sized for the inside of a pin. */
const GLYPH_MOUNTAIN: Glyph = {
  box: "212.274 499.391 24.515 21.867",
  markup:
    '<path d="M222.456 500.589C223.378 498.991 225.684 498.991 226.607 500.589L236.464 517.663C237.387 ' +
    '519.261 236.234 521.258 234.389 521.258H214.673C212.829 521.258 211.676 519.261 212.598 517.663L222.456 ' +
    '500.589Z" fill="{INK}"/>' +
    '<path d="M222.835 504.146C223.611 502.866 225.466 502.861 226.249 504.136L230.392 510.879C231.011 ' +
    "511.888 230.624 513.211 229.557 513.727L229.06 513.967C228.353 514.309 227.511 514.204 226.908 " +
    "513.701L224.626 511.794C223.961 511.239 223.015 511.174 222.281 511.634L221.487 512.132C221.101 " +
    "512.374 220.63 512.437 220.194 512.306C219.206 512.009 218.764 510.859 219.299 509.977L222.835 " +
    '504.146Z" fill="{PAPER}"/>',
};

/** `Vector` — the plane. Single-tone in the kit, so it takes ink only. */
const GLYPH_PLANE: Glyph = {
  box: "112.375 212.359 19.25 22.876",
  markup:
    '<path d="M120.636 222.331C120.622 221.034 120.623 216.055 120.658 214.951C120.728 213.473 121.305 ' +
    "212.359 121.999 212.359C122.694 212.359 123.271 213.473 123.342 214.954C123.375 216.055 123.377 " +
    "221.034 123.363 222.334L131.523 227.305L131.625 227.365L131.625 229.069L124.913 226.988L123.26 " +
    "226.988C123.234 227.63 123.205 228.269 123.176 228.909C123.131 229.906 123.087 230.901 123.05 " +
    "231.902L124.783 233.922L124.783 235.235L121.999 233.418L119.216 235.235L119.216 233.917L120.948 " +
    "231.898C120.91 230.853 120.864 229.809 120.817 228.764C120.79 228.174 120.764 227.581 120.739 " +
    '226.988L119.058 226.988L112.649 228.984L112.375 229.071L112.375 227.365L120.636 222.331Z" ' +
    'fill="{INK}" stroke="{INK}" stroke-linejoin="round"/>',
};

/** `Group 11743_2` — the bus, verbatim including its roof rail, mirrors, wheels and window band. */
const GLYPH_BUS: Glyph = {
  box: "202.864 435.664 26.264 24.227",
  markup:
    '<path d="M226.039 437.114C225.962 436.337 225.326 435.726 224.529 435.664H217.739H214.591H207.549C206.753 ' +
    "435.726 206.116 436.337 206.039 437.114V447.689V453.177C206.039 455.103 207.6 456.664 209.526 " +
    '456.664H222.552C224.478 456.664 226.039 455.103 226.039 453.177V447.689V437.114Z" fill="{INK}" stroke="{INK}"/>' +
    '<path d="M228.554 443.792L228.568 441.164C228.574 440.182 227.779 439.383 226.797 439.383H225.004" ' +
    'stroke="{INK}" stroke-width="0.7" stroke-linecap="round"/>' +
    '<rect x="227.062" y="440.609" width="2.06589" height="4.16468" rx="1.03295" fill="{INK}"/>' +
    '<path d="M203.438 443.792L203.424 441.164C203.418 440.182 204.213 439.383 205.195 439.383H206.989" ' +
    'stroke="{INK}" stroke-width="0.7" stroke-linecap="round"/>' +
    '<rect width="2.06589" height="4.16468" rx="1.03295" transform="matrix(-1 0 0 1 204.93 440.609)" fill="{INK}"/>' +
    '<path d="M221.039 455.891V459.141C221.039 459.34 221.102 459.53 221.215 459.671C221.327 459.812 ' +
    "221.48 459.891 221.639 459.891H223.439C223.598 459.891 223.751 459.812 223.863 459.671C223.976 " +
    '459.53 224.039 459.34 224.039 459.141V455.891" fill="{INK}"/>' +
    '<path d="M208.039 455.891V459.141C208.039 459.34 208.102 459.53 208.215 459.671C208.327 459.812 ' +
    "208.48 459.891 208.639 459.891H210.439C210.598 459.891 210.751 459.812 210.863 459.671C210.976 " +
    '459.53 211.039 459.34 211.039 459.141V455.891" fill="{INK}"/>' +
    '<rect x="206.992" y="439.383" width="18" height="10" rx="1" fill="{PAPER}"/>' +
    '<path d="M223.039 452.383H221.046" stroke="{PAPER}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M211.031 452.383H209.038" stroke="{PAPER}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M211.937 437.932H220.148C220.444 437.932 220.686 437.706 220.686 437.431C220.686 437.155 ' +
    "220.444 436.93 220.148 436.93H211.937C211.641 436.93 211.398 437.155 211.398 437.431C211.398 437.706 " +
    '211.641 437.932 211.937 437.932Z" fill="{PAPER}"/>',
};

const GLYPH_FOR: Record<PinKind, Glyph> = {
  mountain: GLYPH_MOUNTAIN,
  airport: GLYPH_PLANE,
  stop: GLYPH_BUS,
};

// ─────────────────────────────────────────────────────────────────────────────
// GLYPH-ONLY MARKS — the reference shows these alongside the teardrops, so they are part of the same
// source rather than a second module. `Polygon 86` + `Union` (the snow-cap notch) and `Ellipse 260` +
// `Vector`, all verbatim.
// ─────────────────────────────────────────────────────────────────────────────
const MARK_MOUNTAIN = {
  viewBox: "184.656 192.797 50 45",
  aspect: 50 / 45,
  markup:
    '<path d="M205.424 195.263C207.305 191.975 212.008 191.975 213.889 195.263L233.994 230.4C235.875 ' +
    "233.687 233.524 237.797 229.761 237.797H189.551C185.789 237.797 183.437 233.687 185.318 230.4L205.424 " +
    '195.263Z" fill="{INK}"/>' +
    '<path d="M207.965 199.646C208.74 198.358 210.606 198.352 211.388 199.637L222.835 218.439C223.448 ' +
    "219.445 223.062 220.76 222.003 221.276L217.699 223.374C216.988 223.721 216.139 223.616 215.534 " +
    "223.105L208.703 217.346C208.036 216.784 207.082 216.719 206.345 217.185L202.989 219.307C202.498 " +
    "219.618 201.896 219.699 201.339 219.531L199.291 218.909C198.045 218.531 197.487 217.081 198.158 " +
    '215.965L207.965 199.646Z" fill="{PAPER}"/>',
};

const MARK_AIRPORT = {
  viewBox: "109.33 211.128 25.339 25.339",
  aspect: 1,
  markup:
    '<circle cx="122" cy="223.797" r="12.6695" fill="{INK}" stroke="{INK}" stroke-width="2.6611"/>' +
    GLYPH_PLANE.markup.replace(/\{INK\}/g, "{PAPER}"),
};

/**
 * Ink/paper for a treatment.
 *
 * A `mark` has no body to knock the glyph out of, so it is always the category colour on white — which is
 * exactly how the reference draws its grey and pink mountain marks and its teal airport disc.
 */
function inkPaper(colour: string, treatment: PinTreatment, shape: PinShape) {
  if (shape === "mark") return { ink: colour, paper: WHITE };
  return treatment === "filled" ? { ink: WHITE, paper: colour } : { ink: colour, paper: WHITE };
}

const paint = (markup: string, ink: string, paper: string) =>
  markup.replace(/\{INK\}/g, ink).replace(/\{PAPER\}/g, paper);

/**
 * The marker, as an SVG string.
 *
 * WHY A STRING AND NOT A COMPONENT: MapLibre's `Marker` takes a real DOM element rather than React children
 * (maplibre.org/maplibre-gl-js/docs/API/classes/Marker), so `KeylessMap` builds its markers imperatively.
 * Returning markup keeps the ONE source usable from that path without mounting a React root per pin — there
 * are 45 points on the continental view.
 *
 * @param kind  the category. Colour and glyph both follow from it; callers never name either.
 * @param size  the rendered HEIGHT in px. Height, not width, because the call sites that predate this
 *              module sized their `<img>` by height, so the pins keep their established visual weight.
 * @param opts  treatment (`outline` is the founder's ruling and the default) and shape.
 *
 * `data-pin-kind` / `data-pin-treatment` / `data-pin-shape` are stamped on the root so a gate can assert
 * the pin COMPONENT rendered and in which state — rather than sniffing an image filename, which is how a
 * floating triangle passed as a pin for two sprints.
 */
export function pinMarkerSvg(kind: PinKind, size = 30, opts: PinOptions = {}): string {
  const { treatment = "outline", shape = "pin", status } = opts;
  const colour = treatment === "muted" ? GREY : PIN_COLOUR[kind];
  const { ink, paper } = inkPaper(colour, treatment, shape);
  // S36 card 1 — THE GLYPH CARRIES THE STATUS; THE BODY DOES NOT.
  //
  // The ruling is precise and the precision is the point: "pin background stays WHITE, outline stays
  // PINK" — so the pin keeps reading as OUR marker at a glance and the colour that varies is the one
  // inside it, exactly as the list's lamp sits inside the row rather than recolouring it. Only an
  // OUTLINE mountain pin takes it: `filled` is the F227 hover-highlight, where the body is solid pink and
  // the glyph is knocked out in white to read as "lit" — recolouring the knockout there would make the
  // highlight and the status fight for the same ink, and `#e9b949` on `#EF0761` is unreadable besides.
  // Status is a resting-state fact; the highlight is a transient. `muted` is untouched for the same
  // reason: grey means "no data behind this point", which outranks a status claim.
  const glyphInk =
    status && kind === "mountain" && treatment === "outline" && shape === "pin" ? STATUS_COLOUR[status] : ink;
  const attrs =
    `data-pin-kind="${kind}" data-pin-treatment="${treatment}" data-pin-shape="${shape}" ` +
    `data-pin-status="${status && glyphInk !== ink ? status : ""}" ` +
    'xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true"';

  if (shape === "mark") {
    // `stop` has no glyph-only form in the reference, so it falls back to the pin rather than inventing one.
    const mark = kind === "airport" ? MARK_AIRPORT : kind === "mountain" ? MARK_MOUNTAIN : null;
    if (mark) {
      return (
        `<svg ${attrs} width="${round(size * mark.aspect)}" height="${round(size)}" viewBox="${mark.viewBox}">` +
        paint(mark.markup, ink, paper) +
        "</svg>"
      );
    }
  }

  const glyph = GLYPH_FOR[kind];
  const body =
    treatment === "filled"
      ? `<path d="${PIN_PATH}" fill="${colour}"/>`
      : // S35 · F203 — WHITE FILL ON THE PIN BODY. This SUPERSEDES the S33 ruling recorded above
        // ("i dont want fill on the pins"), which was made against a BLANK map: with nothing behind the
        // pin, `fill="none"` and a white fill are indistinguishable. Once F202 put a real basemap under
        // them, an unfilled pin let roads, labels and coastlines run straight through the teardrop and
        // the glyph inside it. The founder walked that and ruled WHITE FILL. The stroke, the colour and
        // the footprint are unchanged — only the hole is closed.
        `<path d="${PIN_PATH}" fill="${WHITE}" stroke="${colour}" stroke-width="${PIN_STROKE}" ` +
        'stroke-linejoin="round" stroke-linecap="round"/>';

  return (
    `<svg ${attrs} width="${round(size * PIN_ASPECT)}" height="${round(size)}" viewBox="${PIN_VIEWBOX}">` +
    body +
    `<svg x="${round(HEAD_CX - GLYPH_BOX / 2)}" y="${round(HEAD_CY - GLYPH_BOX / 2)}" ` +
    `width="${GLYPH_BOX}" height="${GLYPH_BOX}" viewBox="${glyph.box}" overflow="visible">` +
    paint(glyph.markup, glyphInk, paper) +
    "</svg></svg>"
  );
}

const round = (n: number) => Number(n.toFixed(3));

/** Maps the served marker flag onto a category, so callers never branch on colour. */
export function pinKindFor(flag?: "plane" | "stop" | "other"): PinKind {
  // S33 · F194 — "stop" joins the mapping so a BUS STOP on the booking-results map draws the bus pin and,
  // through `PIN_COLOUR`, the red border its info bubble uses. Before this the stop map drew a mountain
  // pin at a bus stop and the bubble beside it was red: the same point described two ways, which is the
  // per-callsite drift this module exists to prevent.
  if (flag === "plane") return "airport";
  if (flag === "stop") return "stop";
  return "mountain";
}

/** The category colour, for surfaces that must match a pin without drawing one (e.g. an info bubble border). */
export function pinColour(kind: PinKind): string {
  return PIN_COLOUR[kind];
}
